import { Button } from "@shared/ui/button";
import { Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  useUpdateConsumer,
  useCategory,
  useSubCategory,
  useCity,
  useUtilityServices,
  useConsumerRelation,
} from "@features/cx/consumerOnboarding/hooks";
import { useToast } from "@shared/ui/use-toast";
import { normalizeDate } from "@shared/utils/dateUtils";

interface EditApplicationDialogProps {
  consumerData: any;
  onSuccess?: () => void;
}

export const EditApplicationDialog = ({
  consumerData,
  onSuccess,
}: EditApplicationDialogProps) => {
  const [open, setOpen] = useState(false);
  const { consumerId } = useParams<{ consumerId: string }>();
  const updateConsumerMutation = useUpdateConsumer();
  const { toast } = useToast();

  const { data: categoryData } = useCategory();
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const { data: subCategoryData } = useSubCategory(selectedCategory);
  const { data: cityData } = useCity();
  const { data: utilityData } = useUtilityServices();

  const { data: consumerRelationData } = useConsumerRelation();

  const relationshipOptions = useMemo(
    () =>
      consumerRelationData?.result?.map((item: any) => ({
        label: item.value,
        value: item.key,
      })) || [],
    [consumerRelationData],
  );

  // Category options
  const categoryOptions = useMemo(
    () =>
      categoryData?.result?.map((category: any) => ({
        value: category.code,
        label: category.name,
      })) || [],
    [categoryData],
  );

  // SubCategory options
  const subCategoryOptions = useMemo(
    () =>
      subCategoryData?.result?.map((subCategory: any) => ({
        value: subCategory.code,
        label: subCategory.name,
      })) || [],
    [subCategoryData],
  );

  // City options
  const cityOptions = useMemo(
    () =>
      cityData?.result?.map((city: any) => ({
        value: city.code,
        label: city.name,
      })) || [],
    [cityData],
  );

  // Utility service options
  const utilityServiceOptions = useMemo(
    () =>
      utilityData
        ?.filter((utility: any) => utility.isActive === true)
        .map((utility: any) => ({
          value: utility.name,
          label: utility.name,
        })) || [],
    [utilityData],
  );

  // Pre-filled data from consumerData prop
  const defaultData = useMemo(() => {
    if (!consumerData?.result) {
      return {
        consumerName: "",
        email: "",
        phone: "",
        ssnTaxId: "",
        category: "",
        subCategory: "",
        vipConsumer: "No",
        coApplicantName: "",
        relationshipToApplicant: "",
        coApplicantPhone: "",
        coApplicantEmail: "",
        serviceHouseUnit: "",
        serviceZipCode: "",
        serviceCity: "",
        serviceStreetAddress: "",
        mailingHouseUnit: "",
        mailingZipCode: "",
        mailingCity: "",
        mailingStreetAddress: "",
        serviceStartDate: "",
        preferredContactMethod: [],
        selectServicesHeader: [],
        propertyType: "",
      };
    }

    const data = consumerData.result;
    const secondaryConsumer = data.additionalData?.secondaryConsumer;
    const serviceAddress =
      data.addressData?.service || data.territoryData?.service;
    const billingAddress =
      data.addressData?.billing || data.territoryData?.billing;

    return {
      consumerName: `${data.firstName || ""} ${data.lastName || ""}`.trim(),
      email: data.email || "",
      phone: data.contactNumber || "",
      ssnTaxId: data.ssnEncrypted || "",
      dateOfBirth: normalizeDate(data.dateOfBirth) || "",
      category: data.category || "",
      subCategory: data.subCategory || "",
      vipConsumer: data.isVip ? "Yes" : "No",
      coApplicantName: secondaryConsumer
        ? `${secondaryConsumer.firstName || ""} ${secondaryConsumer.lastName || ""}`.trim()
        : "",
      relationshipToApplicant: secondaryConsumer?.secondaryRelationship || "",
      coApplicantPhone: secondaryConsumer?.contactNumber || "",
      coApplicantEmail: secondaryConsumer?.email || "",

      // Service Address
      serviceHouseUnit: serviceAddress?.UNIT || "",
      serviceZipCode: serviceAddress?.ZIPCODE || "",
      serviceCity: serviceAddress?.CITY || "",
      serviceStreetAddress: serviceAddress?.ADDRESS || "",

      // Mailing Address
      mailingHouseUnit: billingAddress?.UNIT || "",
      mailingZipCode: billingAddress?.ZIPCODE || "",
      mailingCity: billingAddress?.CITY || "",
      mailingStreetAddress: billingAddress?.ADDRESS || "",

      serviceStartDate: data?.registrationDate
        ? normalizeDate(data?.registrationDate)
        : "",
      preferredContactMethod: data.preferredContactMethod || [],
      selectServicesHeader: data.utilityService || [],
      propertyType: data.category || "",
    };
  }, [consumerData]);

  // Set selected category when data changes
  useEffect(() => {
    if (consumerData?.result?.category) {
      setSelectedCategory(consumerData.result.category);
    }
  }, [consumerData]);

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      // Consumer Information Section
      {
        name: "consumerName",
        label: "Consumer Name",
        type: "text",
        placeholder: "Enter consumer name",
        group: "Consumer Information",
        groupColorNeeded: false,
        defaultValue: defaultData.consumerName,
      },
      {
        name: "email",
        label: "Email",
        type: "email",
        disabled: true,
        placeholder: "Enter email",
        group: "Consumer Information",
        groupColorNeeded: false,
        defaultValue: defaultData.email,
      },
      {
        name: "phone",
        label: "Phone",
        type: "country-phone",
        placeholder: "Enter phone number",
        group: "Consumer Information",
        groupColorNeeded: false,
        defaultValue: defaultData.phone,
      },
      {
        name: "ssnTaxId",
        label: "SSN/Tax ID",
        type: "text",
        placeholder: "Enter SSN/Tax ID",
        group: "Consumer Information",
        groupColorNeeded: false,
        defaultValue: defaultData.ssnTaxId,
      },
      {
        name: "dateOfBirth",
        label: "Date Of Birth",
        type: "datepicker",
        placeholder: "Enter DOB",
        group: "Consumer Information",
        groupColorNeeded: false,
      },
      {
        name: "category",
        label: "Category",
        type: "select",
        placeholder: "Select category",
        group: "Consumer Information",
        groupColorNeeded: false,
        options: categoryOptions,
        defaultValue: defaultData.category,
      },
      {
        name: "subCategory",
        label: "Sub Category",
        type: "select",
        placeholder: "Select sub category",
        group: "Consumer Information",
        groupColorNeeded: false,
        options: subCategoryOptions,
        disabled: !selectedCategory,
        defaultValue: defaultData.subCategory,
      },
      {
        name: "vipConsumer",
        label: "VIP Consumer",
        type: "select",
        placeholder: "Select option",
        group: "Consumer Information",
        groupColorNeeded: false,
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
        ],
        defaultValue: defaultData.vipConsumer,
      },

      // Co-Applicant Information Section
      {
        name: "coApplicantName",
        label: "Co-Applicant Name",
        type: "text",
        placeholder: "Enter co-applicant name",
        group: "Co-Applicant Information",
        groupColorNeeded: false,
        defaultValue: defaultData.coApplicantName,
      },
      {
        name: "relationshipToApplicant",
        label: "Relationship to Applicant",
        type: "select",
        placeholder: "Select relationship",
        group: "Co-Applicant Information",
        groupColorNeeded: false,
        options: relationshipOptions,
        defaultValue: defaultData.relationshipToApplicant,
      },
      {
        name: "coApplicantPhone",
        label: "Co-Applicant Phone",
        type: "country-phone",
        placeholder: "Enter co-applicant phone",
        group: "Co-Applicant Information",
        groupColorNeeded: false,
        defaultValue: defaultData.coApplicantPhone,
      },
      {
        name: "coApplicantEmail",
        label: "Co-Applicant Email",
        type: "email",
        placeholder: "Enter co-applicant email",
        group: "Co-Applicant Information",
        groupColorNeeded: false,
        defaultValue: defaultData.coApplicantEmail,
      },

      // Service Address Section
      {
        name: "serviceHouseUnit",
        label: "House/Unit Number",
        type: "text",
        placeholder: "e.g. 123, Apt 4B",
        group: "Service Address",
        groupColorNeeded: false,
        defaultValue: defaultData.serviceHouseUnit,
      },
      {
        name: "serviceZipCode",
        label: "Zip/Postal Code",
        type: "text",
        placeholder: "e.g. 12345",
        group: "Service Address",
        groupColorNeeded: false,
        defaultValue: defaultData.serviceZipCode,
      },
      {
        name: "serviceCity",
        label: "City",
        type: "select",
        placeholder: "Select city",
        options: cityOptions,
        group: "Service Address",
        groupColorNeeded: false,
        defaultValue: defaultData.serviceCity,
      },
      {
        name: "serviceStreetAddress",
        label: "Street Address",
        type: "text",
        placeholder: "e.g. 123 Main Street",
        group: "Service Address",
        groupColorNeeded: false,
        defaultValue: defaultData.serviceStreetAddress,
      },

      // Mailing Address Section
      {
        name: "mailingHouseUnit",
        label: "House/Unit Number",
        type: "text",
        placeholder: "e.g. 123, Apt 4B",
        group: "Mailing Address",
        groupColorNeeded: false,
        defaultValue: defaultData.mailingHouseUnit,
      },
      {
        name: "mailingZipCode",
        label: "Zip/Postal Code",
        type: "text",
        placeholder: "e.g. 12345",
        group: "Mailing Address",
        groupColorNeeded: false,
        defaultValue: defaultData.mailingZipCode,
      },
      {
        name: "mailingCity",
        label: "City",
        type: "select",
        placeholder: "Select city",
        options: cityOptions,
        group: "Mailing Address",
        groupColorNeeded: false,
        defaultValue: defaultData.mailingCity,
      },
      {
        name: "mailingStreetAddress",
        label: "Street Address",
        type: "text",
        placeholder: "e.g. 123 Main Street",
        group: "Mailing Address",
        groupColorNeeded: false,
        defaultValue: defaultData.mailingStreetAddress,
      },

      // Service Information Section
      {
        name: "serviceStartDate",
        label: "Service Start Date",
        type: "datepicker",
        placeholder: "Select date",
        group: "Service Information",
        groupColorNeeded: false,
        defaultValue: defaultData.serviceStartDate,
      },
      {
        name: "preferredContactMethod",
        label: "Preferred Contact Method",
        type: "multi-select",
        placeholder: "Select contact method",
        group: "Service Information",
        groupColorNeeded: false,
        options: [
          { label: "Email", value: "Email" },
          { label: "Phone", value: "Phone" },
        ],
        defaultValue: defaultData.preferredContactMethod,
      },
      {
        name: "selectServicesHeader",
        label: "Select Services",
        type: "multi-select",
        placeholder: "Select services",
        group: "Service Information",
        groupColorNeeded: false,
        options: utilityServiceOptions,
        defaultValue: defaultData.selectServicesHeader,
      },
    ],
    [
      defaultData,
      categoryOptions,
      subCategoryOptions,
      selectedCategory,
      cityOptions,
      utilityServiceOptions,
    ],
  );

  // Create form using FormService
  const form = FormService.createForm({
    fields: formFields,
    defaultValues: defaultData,
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open && consumerData?.result) {
      form.reset(defaultData);
    }
  }, [open, consumerData, defaultData, form]);

  // Transform form data to API payload format
  const transformToPayload = (formData: any) => {
    // Split consumer name into first and last name
    const nameParts = formData.consumerName?.trim().split(" ") || [];
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    // Split co-applicant name
    const coApplicantParts = formData.coApplicantName?.trim().split(" ") || [];
    const coFirstName = coApplicantParts[0] || "";
    const coLastName = coApplicantParts.slice(1).join(" ") || "";
    const getRelationshipLabel = () => {
      return (
        relationshipOptions.find(
          (option) => option.value == formData.relationshipToApplicant,
        )?.label || ""
      );
    };
    const relationshipLabel = getRelationshipLabel();
    return {
      consumer_details: {
        primary_consumer: {
          first_name: firstName,
          last_name: lastName,
          email: formData.email,
          is_vip: formData.vipConsumer === "Yes",
          contact_number: formData.phone,
          ssn_encrypted: formData.ssnTaxId,
          date_of_birth: normalizeDate(formData.dateOfBirth),
          tags: consumerData?.result?.tags || [],
          preferred_contact_method: formData.preferredContactMethod || [],
          utility_service: formData.selectServicesHeader || [],
        },
        secondary_consumer: {
          first_name: coFirstName,
          last_name: coLastName,
          email: formData.coApplicantEmail || "",
          contact_number: formData.coApplicantPhone || "",
          secondary_relationship: formData.relationshipToApplicant || "",
          secondary_consumer_display: relationshipLabel,
        },
        billing_address_data: {
          zipcode: formData.mailingZipCode || "",
          city: formData.mailingCity || "",
          unit: formData.mailingHouseUnit || "",
          address: formData.mailingStreetAddress || "",
        },
      },
      remote_utility_id: consumerData?.result?.remoteUtilityId,
      source: String(consumerData?.result?.source || "0"),
    };
  };

  // Handle field change
  const handleFieldChange = (fieldName: string, value: any) => {
    // Handle category change - load subcategories
    if (fieldName === "category") {
      setSelectedCategory(value);
      if (!value) {
        form.setValue("subCategory", "", {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (data: any) => {
    try {
      console.log("=== Form Submitted ===");
      console.log("All Form Values:", data);

      const payload = transformToPayload(data);
      console.log("Transformed Payload:", JSON.stringify(payload, null, 2));

      // Call the update API
      await updateConsumerMutation.mutateAsync({
        consumerId: consumerId || String(consumerData?.result?.id),
        payload,
      });

      toast({
        title: "Success",
        description: "Consumer updated successfully",
      });

      setOpen(false);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error updating consumer:", error);
      toast({
        title: "Error",
        description: "Failed to update consumer",
        variant: "destructive",
      });
    }
  };

  // Handle cancel
  const handleCancel = () => {
    console.log("Form cancelled");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-start border bg-gray-50 hover:bg-gray-100 text-gray-900 rounded-lg"
        >
          <Pencil className="h-4 w-4 mr-2" />
          Edit Application
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Application Details</DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
              lg: 2,
            }}
            config={{
              submitButtonText: "Save Changes",
              showResetButton: true,
              resetButtonText: "Cancel",
              classes: FIELD_CLASSES,
            }}
            onReset={handleCancel}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
